// src/components/finance/BudgetManager.tsx
// DROP 32 — Enhanced Budget Module (8 items)
// 1. L3 ₦76,650,000 preload  2. Editable heads  3. Participant count + unit cost
// 4. Attendee categories  5. Activity level toggle  6. Accounting live link
// 7. Coherence engine + variance warnings  8. Estimated amounts + inline editing

import { printBudgetPlan, printVarianceReport } from "../../utils/printUtils";
import PrintButton from "../shared/PrintButton";
import React, { useState, useEffect, useCallback } from "react";
import {
  LayoutDashboard, PlusCircle, CheckCircle, XCircle,
  Clock, AlertTriangle, RefreshCw, FileText,
  Shield, Crown, Users, DollarSign, BarChart3,
  Edit3, Trash2, Send, ThumbsUp, ThumbsDown, Eye,
  Save, X, Plus, Link,
} from "lucide-react";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:"#070D0A", surface:"#0E1812", card:"#121E16", border:"#1C2E22",
  text:"#EDE8DC", muted:"#9CA38F", green:"#0F5132",
  gold:"#C9A84C", goldLt:"#E2C97E", red:"#ef4444",
  orange:"#f97316", blue:"#60a5fa", purple:"#A78BFA",
};

// ── Types ─────────────────────────────────────────────────────────────────────
type BudgetLevel  = "regular"|"medium"|"grand";
type BudgetStatus = "draft"|"cwc_review"|"board_review"|"authorized"|"active"|"rejected";
type ActivityLevel = "L1"|"L2"|"L3";

interface AttendeeCategory { type:"VIP"|"General"|"Youth"|"Diaspora"; count:number; unitCost:number; }

interface LineItem {
  id:string; category:string; description:string;
  estimatedAmount:number; budgeted:number; actual:number;
  participants:number; unitCost:number;
  attendeeCategories:AttendeeCategory[];
  activityLevel:ActivityLevel; isCustom?:boolean;
}

interface RevenueSource {
  id:string; source:string;
  estimatedAmount:number; projected:number; received:number;
  isCustom?:boolean;
}

interface ApprovalStep {
  step:string; actor:string; role:string;
  action:string; timestamp:string; notes?:string;
}

interface BudgetPlan {
  id:string; title:string; level:BudgetLevel; status:BudgetStatus;
  totalBudgeted:number; totalActual:number;
  totalEstimatedExpenditure:number; totalEstimatedRevenue:number;
  lineItems:LineItem[]; revenueSources:RevenueSource[];
  approvalHistory:ApprovalStep[];
  createdBy:string; createdAt:string; updatedAt:string; notes?:string;
  cwcApprovedBy?:string; cwcApprovedAt?:string;
  boardAuthorizedBy?:string; boardAuthorizedAt?:string;
}

interface CurrentUser { email?:string; name?:string; systemRole?:string; role?:string; permissions?:string[]; }
interface Props { currentUser?:CurrentUser|null; }

// ── Constants ─────────────────────────────────────────────────────────────────
const BUDGET_LEVELS:Record<BudgetLevel,{label:string;range:string;color:string;emoji:string;min:number;max:number;approver:string}> = {
  regular:{ label:"Regular Budget", range:"₦2M–₦5M",    color:"#4ADE80", emoji:"🟢", min:2_000_000,  max:5_000_000,   approver:"CWC Chairman" },
  medium: { label:"Medium Budget",  range:"₦10M–₦25M",  color:C.gold,    emoji:"🟡", min:10_000_000, max:25_000_000,  approver:"CWC + Board Vice-Chairman" },
  grand:  { label:"Grand Budget",   range:"₦50M–₦100M", color:"#F472B6", emoji:"🔴", min:50_000_000, max:100_000_000, approver:"Full Board Vote" },
};

const ACTIVITY_LEVELS:Record<ActivityLevel,{label:string;color:string}> = {
  L1:{ label:"L1 — Regular", color:"#4ADE80" },
  L2:{ label:"L2 — Medium",  color:C.gold    },
  L3:{ label:"L3 — Grand",   color:"#F472B6" },
};

const DEFAULT_CATEGORIES = [
  "Programme & Entertainment","Logistics & Transport","Media & Broadcasting",
  "Hospitality & Catering","Security & Safety","Administration & Secretariat",
  "Venue & Infrastructure","Marketing & Publicity","Awards & Prizes","Contingency Reserve",
];

const DEFAULT_REVENUE = [
  "Donations & Pledges","Raffle Ticket Sales","Festival Store Sales",
  "Corporate Sponsorships","LOC Village Contributions","Government Grants",
  "Diaspora Remittances","Vendor Registration Fees","Gate / Event Tickets","Other Income",
];

const ATTENDEE_TYPES:AttendeeCategory["type"][] = ["VIP","General","Youth","Diaspora"];

const STATUS_CONFIG:Record<BudgetStatus,{label:string;color:string;icon:React.ReactNode}> = {
  draft:        { label:"Draft",            color:C.muted,   icon:<Edit3 size={13}/> },
  cwc_review:   { label:"CWC Review",       color:C.orange,  icon:<Clock size={13}/> },
  board_review: { label:"Board Review",     color:C.blue,    icon:<Clock size={13}/> },
  authorized:   { label:"Board Authorized", color:C.purple,  icon:<CheckCircle size={13}/> },
  active:       { label:"Active",           color:"#4ADE80", icon:<CheckCircle size={13}/> },
  rejected:     { label:"Rejected",         color:C.red,     icon:<XCircle size={13}/> },
};

// ── L3 Grand Preset — ₦76,650,000 ────────────────────────────────────────────
const L3_ITEMS = [
  { category:"Programme & Entertainment", description:"Opening ceremony, cultural displays, Miss ERUE pageant, grand gala",        estimatedAmount:12_000_000 },
  { category:"Logistics & Transport",     description:"VIP shuttles, equipment haulage, generator hire, logistics fleet",           estimatedAmount:8_500_000  },
  { category:"Media & Broadcasting",      description:"Live streaming, OB van, photography, social media team",                    estimatedAmount:6_000_000  },
  { category:"Hospitality & Catering",    description:"VIP hospitality, general catering, drinks & refreshments",                  estimatedAmount:10_000_000 },
  { category:"Security & Safety",         description:"Professional security, medical team, crowd management",                     estimatedAmount:5_500_000  },
  { category:"Administration & Secretariat", description:"Secretariat operations, printing, communications, staff costs",          estimatedAmount:4_200_000  },
  { category:"Venue & Infrastructure",    description:"Stage, lighting, PA system, tents, seating, décor",                        estimatedAmount:14_000_000 },
  { category:"Marketing & Publicity",     description:"Billboards, radio/TV adverts, online campaigns, souvenirs",                 estimatedAmount:5_000_000  },
  { category:"Awards & Prizes",           description:"Raffle prizes, competition prizes, certificates, trophies",                 estimatedAmount:7_000_000  },
  { category:"Contingency Reserve",       description:"10% contingency buffer for unforeseen expenses",                            estimatedAmount:4_450_000  },
];

const L3_REVENUE = [
  { source:"Donations & Pledges",       estimatedAmount:20_000_000 },
  { source:"Corporate Sponsorships",    estimatedAmount:18_000_000 },
  { source:"Raffle Ticket Sales",       estimatedAmount:12_000_000 },
  { source:"LOC Village Contributions", estimatedAmount:10_000_000 },
  { source:"Diaspora Remittances",      estimatedAmount:8_000_000  },
  { source:"Vendor Registration Fees",  estimatedAmount:3_000_000  },
  { source:"Gate / Event Tickets",      estimatedAmount:5_000_000  },
  { source:"Festival Store Sales",      estimatedAmount:2_500_000  },
  { source:"Government Grants",         estimatedAmount:3_000_000  },
  { source:"Other Income",              estimatedAmount:1_500_000  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n:number) => "₦"+n.toLocaleString("en-NG",{minimumFractionDigits:0,maximumFractionDigits:0});
const pct = (a:number,b:number) => b>0?((a/b)*100).toFixed(1)+"%":"—";
const vari = (b:number,a:number) => b-a;
const uid  = () => Math.random().toString(36).slice(2,10);
const authHeader = () => {
  const t = localStorage.getItem("erue_auth_token")||sessionStorage.getItem("erue_auth_token")||"";
  return {"Content-Type":"application/json", Authorization:`Bearer ${t}`};
};

function defaultCats():AttendeeCategory[] { return ATTENDEE_TYPES.map(type=>({type,count:0,unitCost:0})); }

function makeL3Items():LineItem[] {
  return L3_ITEMS.map((p,i)=>({
    id:`li_${i}`, category:p.category, description:p.description,
    estimatedAmount:p.estimatedAmount, budgeted:p.estimatedAmount, actual:0,
    participants:0, unitCost:0, attendeeCategories:defaultCats(), activityLevel:"L3" as ActivityLevel,
  }));
}

function makeL3Revenue():RevenueSource[] {
  return L3_REVENUE.map((r,i)=>({
    id:`rs_${i}`, source:r.source,
    estimatedAmount:r.estimatedAmount, projected:r.estimatedAmount, received:0,
  }));
}

function makeBlankItems():LineItem[] {
  return DEFAULT_CATEGORIES.map((cat,i)=>({
    id:`li_${i}`, category:cat, description:"",
    estimatedAmount:0, budgeted:0, actual:0, participants:0, unitCost:0,
    attendeeCategories:defaultCats(), activityLevel:"L2" as ActivityLevel,
  }));
}

function makeBlankRevenue():RevenueSource[] {
  return DEFAULT_REVENUE.map((src,i)=>({
    id:`rs_${i}`, source:src, estimatedAmount:0, projected:0, received:0,
  }));
}

function computeEst(item:LineItem):number {
  const catTotal = item.attendeeCategories.reduce((s,c)=>s+c.count*c.unitCost,0);
  if (catTotal>0) return catTotal;
  if (item.participants>0&&item.unitCost>0) return item.participants*item.unitCost;
  return item.estimatedAmount;
}

// ── Coherence Engine ──────────────────────────────────────────────────────────
interface Warning { type:"error"|"warning"|"info"; msg:string; }

function coherenceCheck(items:LineItem[], revenue:RevenueSource[], level:BudgetLevel):Warning[] {
  const cfg = BUDGET_LEVELS[level];
  const totalEst = items.reduce((s,i)=>s+computeEst(i),0);
  const totalRev = revenue.reduce((s,r)=>s+(r.estimatedAmount||0),0);
  const surplus  = totalRev-totalEst;
  const W:Warning[] = [];
  if (totalEst>0&&totalEst<cfg.min)
    W.push({type:"warning",msg:`Estimated expenditure ${fmt(totalEst)} is below ${cfg.label} minimum ${fmt(cfg.min)}`});
  if (totalEst>cfg.max)
    W.push({type:"error",msg:`Estimated expenditure ${fmt(totalEst)} exceeds ${cfg.label} maximum ${fmt(cfg.max)}`});
  if (totalRev>0&&surplus<0)
    W.push({type:"error",msg:`DEFICIT of ${fmt(Math.abs(surplus))} — revenue ${fmt(totalRev)} < expenditure ${fmt(totalEst)}`});
  if (totalRev>0&&surplus>0&&surplus/totalEst>0.3)
    W.push({type:"info",msg:`Large projected surplus ${fmt(surplus)} — consider reinvesting in quality or reserves`});
  const cont = items.find(i=>i.category.toLowerCase().includes("contingency"));
  if (cont&&totalEst>0&&computeEst(cont)/totalEst<0.05)
    W.push({type:"warning",msg:`Contingency reserve is below recommended 10% — suggest ${fmt(totalEst*0.1)}`});
  return W;
}

// ── Sub-components ────────────────────────────────────────────────────────────
function Stat({label,value,sub,color}:{label:string;value:string;sub?:string;color?:string}) {
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",flex:1,minWidth:150}}>
      <div style={{fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>{label}</div>
      <div style={{fontSize:22,fontWeight:700,color:color||C.gold,fontFamily:"Cinzel,serif"}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:C.muted,marginTop:4}}>{sub}</div>}
    </div>
  );
}

function SBadge({status}:{status:BudgetStatus}) {
  const c=STATUS_CONFIG[status];
  return <span style={{display:"inline-flex",alignItems:"center",gap:4,padding:"3px 10px",borderRadius:20,background:c.color+"22",border:`1px solid ${c.color}44`,color:c.color,fontSize:11,fontWeight:600}}>{c.icon}{c.label}</span>;
}

function LBadge({level}:{level:ActivityLevel}) {
  const c=ACTIVITY_LEVELS[level];
  return <span style={{display:"inline-block",padding:"2px 8px",borderRadius:10,background:c.color+"22",border:`1px solid ${c.color}44`,color:c.color,fontSize:10,fontWeight:700}}>{level}</span>;
}

function VBar({budgeted,actual}:{budgeted:number;actual:number}) {
  const r=budgeted>0?Math.min((actual/budgeted)*100,100):0;
  const over=actual>budgeted;
  const col=over?C.red:r>80?C.orange:"#4ADE80";
  return (
    <div style={{marginTop:4}}>
      <div style={{height:5,background:C.border,borderRadius:3,overflow:"hidden"}}>
        <div style={{height:"100%",width:`${r}%`,background:col,borderRadius:3,transition:"width 0.4s"}}/>
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:3,fontSize:10,color:C.muted}}>
        <span>Actual: {fmt(actual)}</span>
        <span style={{color:over?C.red:col}}>{pct(actual,budgeted)}</span>
      </div>
    </div>
  );
}

function CoherencePanel({warnings}:{warnings:Warning[]}) {
  if (!warnings.length) return null;
  const cols={error:C.red,warning:C.orange,info:C.blue};
  const icons={error:"🔴",warning:"⚠️",info:"ℹ️"};
  return (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {warnings.map((w,i)=>(
        <div key={i} style={{background:cols[w.type]+"18",border:`1px solid ${cols[w.type]}44`,borderRadius:8,padding:"8px 14px",display:"flex",gap:8}}>
          <span style={{fontSize:13,flexShrink:0}}>{icons[w.type]}</span>
          <span style={{fontSize:12,color:cols[w.type],lineHeight:1.5}}>{w.msg}</span>
        </div>
      ))}
    </div>
  );
}

// ── Inline editable amount ────────────────────────────────────────────────────
function InlineAmt({value,onChange,color,label}:{value:number;onChange:(v:number)=>void;color?:string;label?:string}) {
  const [editing,setEditing]=useState(false);
  const [draft,setDraft]=useState(String(value));
  const commit=()=>{const n=parseFloat(draft.replace(/[^\d.]/g,""))||0;onChange(n);setEditing(false);};
  if (editing) return (
    <div style={{display:"flex",alignItems:"center",gap:4}}>
      <span style={{fontSize:11,color:C.muted}}>₦</span>
      <input autoFocus value={draft}
        onChange={e=>setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditing(false);}}
        style={{width:110,padding:"4px 8px",background:C.surface,border:`1px solid ${C.gold}`,borderRadius:4,color:color||C.gold,fontSize:12,textAlign:"right"}}/>
    </div>
  );
  return (
    <button onClick={()=>{setDraft(String(value));setEditing(true);}} title={`Click to edit${label?" "+label:""}`}
      style={{background:"none",border:"none",cursor:"pointer",padding:0,display:"flex",alignItems:"center",gap:4}}>
      <span style={{fontSize:13,fontWeight:700,color:color||C.gold}}>{fmt(value)}</span>
      <Edit3 size={10} style={{color:C.muted,opacity:0.5}}/>
    </button>
  );
}

// ── Item Editor Row ───────────────────────────────────────────────────────────
function ItemRow({item,idx,items,setItems,expanded,onToggle}:{
  key?:React.Key;
  item:LineItem;idx:number;items:LineItem[];
  setItems:(v:LineItem[])=>void;expanded:boolean;onToggle:()=>void;
}) {
  const est=computeEst(item);
  const iS=(extra?:React.CSSProperties):React.CSSProperties=>({
    padding:"5px 8px",background:C.surface,border:`1px solid ${C.border}`,
    borderRadius:4,color:C.text,fontSize:11,boxSizing:"border-box",...extra,
  });
  const upd=(patch:Partial<LineItem>)=>{const c=[...items];c[idx]={...c[idx],...patch};setItems(c);};
  return (
    <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,overflow:"hidden"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",padding:"10px 12px",flexWrap:"wrap"}}>
        <select value={item.activityLevel} onChange={e=>upd({activityLevel:e.target.value as ActivityLevel})}
          style={{...iS({width:56,color:ACTIVITY_LEVELS[item.activityLevel].color})}}>
          {(["L1","L2","L3"] as ActivityLevel[]).map(l=><option key={l} value={l}>{l}</option>)}
        </select>
        {item.isCustom
          ? <input value={item.category} onChange={e=>upd({category:e.target.value})} style={iS({flex:2,minWidth:100})} placeholder="Category"/>
          : <div style={{flex:2,fontSize:12,color:C.text,fontWeight:600,minWidth:100}}>{item.category}</div>
        }
        <input value={item.description} onChange={e=>upd({description:e.target.value})} style={iS({flex:2,minWidth:100})} placeholder="Description"/>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",minWidth:110}}>
          <div style={{fontSize:9,color:C.muted,marginBottom:2}}>ESTIMATED</div>
          <InlineAmt value={item.estimatedAmount} onChange={v=>upd({estimatedAmount:v})} label="estimated"/>
          {est!==item.estimatedAmount&&<div style={{fontSize:9,color:C.blue}}>Auto:{fmt(est)}</div>}
        </div>
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",minWidth:110}}>
          <div style={{fontSize:9,color:C.muted,marginBottom:2}}>BUDGETED</div>
          <InlineAmt value={item.budgeted} onChange={v=>upd({budgeted:v})} color="#4ADE80" label="budgeted"/>
        </div>
        <button onClick={onToggle} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:4,color:C.muted,padding:"4px 8px",cursor:"pointer",fontSize:11}}>
          {expanded?"▲":"▼"}
        </button>
        {item.isCustom&&<button onClick={()=>setItems(items.filter(i=>i.id!==item.id))} style={{background:"none",border:`1px solid ${C.red}33`,borderRadius:4,color:C.red,padding:"4px 8px",cursor:"pointer"}}><X size={12}/></button>}
      </div>
      {expanded&&(
        <div style={{padding:"10px 14px",borderTop:`1px solid ${C.border}`,background:C.surface,display:"flex",flexDirection:"column",gap:10}}>
          <div style={{display:"flex",gap:12,flexWrap:"wrap",alignItems:"center"}}>
            <div>
              <div style={{fontSize:9,color:C.muted,marginBottom:3}}>PARTICIPANTS</div>
              <input type="number" value={item.participants||""} onChange={e=>upd({participants:Number(e.target.value)})} style={iS({width:90})} placeholder="0"/>
            </div>
            <div>
              <div style={{fontSize:9,color:C.muted,marginBottom:3}}>UNIT COST (₦)</div>
              <input type="number" value={item.unitCost||""} onChange={e=>upd({unitCost:Number(e.target.value)})} style={iS({width:110})} placeholder="0"/>
            </div>
            {item.participants>0&&item.unitCost>0&&(
              <div style={{fontSize:11,color:C.blue}}>
                Auto: {fmt(item.participants*item.unitCost)}
                <button onClick={()=>upd({estimatedAmount:item.participants*item.unitCost,budgeted:item.participants*item.unitCost})}
                  style={{marginLeft:6,fontSize:10,background:C.blue+"22",border:`1px solid ${C.blue}`,color:C.blue,borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>Apply</button>
              </div>
            )}
          </div>
          <div>
            <div style={{fontSize:9,color:C.muted,marginBottom:5}}>ATTENDEE CATEGORIES</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(190px,1fr))",gap:6}}>
              {item.attendeeCategories.map((cat,ci)=>(
                <div key={cat.type} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"8px 10px"}}>
                  <div style={{fontSize:10,color:C.gold,fontWeight:700,marginBottom:4}}>{cat.type}</div>
                  <div style={{display:"flex",gap:6}}>
                    <input type="number" value={cat.count||""} placeholder="Count"
                      onChange={e=>{const cats=[...item.attendeeCategories];cats[ci]={...cats[ci],count:Number(e.target.value)};upd({attendeeCategories:cats});}}
                      style={{width:56,...iS({fontSize:10})}}/>
                    <input type="number" value={cat.unitCost||""} placeholder="₦ ea"
                      onChange={e=>{const cats=[...item.attendeeCategories];cats[ci]={...cats[ci],unitCost:Number(e.target.value)};upd({attendeeCategories:cats});}}
                      style={{width:76,...iS({fontSize:10})}}/>
                    {cat.count>0&&cat.unitCost>0&&<span style={{fontSize:10,color:C.blue,alignSelf:"center"}}>{fmt(cat.count*cat.unitCost)}</span>}
                  </div>
                </div>
              ))}
            </div>
            {item.attendeeCategories.reduce((s,c)=>s+c.count*c.unitCost,0)>0&&(
              <div style={{fontSize:11,color:C.blue,marginTop:6}}>
                Cat total: {fmt(item.attendeeCategories.reduce((s,c)=>s+c.count*c.unitCost,0))}
                <button onClick={()=>{const t=item.attendeeCategories.reduce((s,c)=>s+c.count*c.unitCost,0);upd({estimatedAmount:t,budgeted:t});}}
                  style={{marginLeft:6,fontSize:10,background:C.blue+"22",border:`1px solid ${C.blue}`,color:C.blue,borderRadius:4,padding:"2px 6px",cursor:"pointer"}}>Apply</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BudgetManager({currentUser}:Props) {
  const [plans,        setPlans]       = useState<BudgetPlan[]>([]);
  const [loading,      setLoading]     = useState(true);
  const [error,        setError]       = useState<string|null>(null);
  const [tab,          setTab]         = useState<"overview"|"plans"|"new"|"detail"|"variance"|"accounting">("overview");
  const [selected,     setSelected]    = useState<BudgetPlan|null>(null);
  const [editing,      setEditing]     = useState<BudgetPlan|null>(null);
  const [fLevel,       setFLevel]      = useState<BudgetLevel|"all">("all");
  const [fStatus,      setFStatus]     = useState<BudgetStatus|"all">("all");
  const [saving,       setSaving]      = useState(false);
  const [msg,          setMsg]         = useState<string|null>(null);
  const [accTx,        setAccTx]       = useState<any[]>([]);
  const [expandedIdx,  setExpandedIdx] = useState<number|null>(null);

  // New plan form
  const [nLevel,   setNLevel]   = useState<BudgetLevel>("grand");
  const [nTitle,   setNTitle]   = useState("ERUE Festival 2026 — Grand Budget");
  const [nNotes,   setNNotes]   = useState("");
  const [nItems,   setNItems]   = useState<LineItem[]>(makeL3Items);
  const [nRevenue, setNRevenue] = useState<RevenueSource[]>(makeL3Revenue);

  const role    = (currentUser?.systemRole||currentUser?.role||"").toUpperCase();
  const isAdmin = ["PLATFORM_ADMIN","FESTIVAL_COORDINATOR"].includes(role);
  const isCWC   = isAdmin||role==="ACCOUNTANT";
  const isBoard = role==="PLATFORM_ADMIN";

  const showMsg=(m:string)=>{setMsg(m);setTimeout(()=>setMsg(null),4000);};

  const fetchPlans=useCallback(async()=>{
    setLoading(true);setError(null);
    try{const r=await fetch("/api/admin/budget/plans",{headers:authHeader()});const d=await r.json();if(d.success)setPlans(d.plans||[]);else setError(d.error||"Failed");}
    catch{setError("Network error");}finally{setLoading(false);}
  },[]);

  const fetchAcc=useCallback(async()=>{
    try{const r=await fetch("/api/admin/accounting/transactions",{headers:authHeader()});const d=await r.json();if(d.success)setAccTx(d.transactions||d.items||[]);}
    catch{/*silent*/}
  },[]);

  useEffect(()=>{fetchPlans();fetchAcc();},[fetchPlans,fetchAcc]);

  const activePlans  = plans.filter(p=>p.status==="active");
  const pendingPlans = plans.filter(p=>["cwc_review","board_review"].includes(p.status));
  const totBudg      = activePlans.reduce((s,p)=>s+p.totalBudgeted,0);
  const totAct       = activePlans.reduce((s,p)=>s+p.totalActual,0);

  const handleCreate=async()=>{
    if(!nTitle.trim()){showMsg("❌ Plan title required");return;}
    const totEst  = nItems.reduce((s,i)=>s+computeEst(i),0);
    const totBu   = nItems.reduce((s,i)=>s+i.budgeted,0);
    const totRev  = nRevenue.reduce((s,r)=>s+r.estimatedAmount,0);
    setSaving(true);
    try{
      const r=await fetch("/api/admin/budget/plans",{method:"POST",headers:authHeader(),
        body:JSON.stringify({title:nTitle.trim(),level:nLevel,notes:nNotes.trim(),
          lineItems:nItems,revenueSources:nRevenue,
          totalEstimatedExpenditure:totEst,totalEstimatedRevenue:totRev,totalBudgeted:totBu})});
      const d=await r.json();
      if(d.success){showMsg("✅ Budget plan created");setTab("plans");setNTitle("ERUE Festival 2026 — Grand Budget");setNNotes("");setNItems(makeL3Items());setNRevenue(makeL3Revenue());setExpandedIdx(null);fetchPlans();}
      else showMsg("❌ "+(d.error||"Failed"));
    }catch{showMsg("❌ Network error");}finally{setSaving(false);}
  };

  const handleSaveEdit=async()=>{
    if(!editing)return;
    setSaving(true);
    try{
      const totEst  = editing.lineItems.reduce((s,i)=>s+computeEst(i),0);
      const totBu   = editing.lineItems.reduce((s,i)=>s+i.budgeted,0);
      const totRev  = editing.revenueSources.reduce((s,r)=>s+r.estimatedAmount,0);
      const r=await fetch(`/api/admin/budget/plans/${editing.id}`,{method:"PUT",headers:authHeader(),
        body:JSON.stringify({...editing,totalBudgeted:totBu,totalEstimatedExpenditure:totEst,totalEstimatedRevenue:totRev})});
      const d=await r.json();
      if(d.success){showMsg("✅ Plan updated");setSelected(d.plan||editing);setEditing(null);setExpandedIdx(null);fetchPlans();}
      else showMsg("❌ "+d.error);
    }catch{showMsg("❌ Network error");}finally{setSaving(false);}
  };

  const workflowAction=async(planId:string,action:string)=>{
    setSaving(true);
    try{
      const r=await fetch(`/api/admin/budget/plans/${planId}/action`,{method:"POST",headers:authHeader(),body:JSON.stringify({action})});
      const d=await r.json();
      if(d.success){showMsg("✅ "+action+" successful");fetchPlans();if(selected?.id===planId)setSelected(d.plan||null);}
      else showMsg("❌ "+(d.error||"Failed"));
    }catch{showMsg("❌ Network error");}finally{setSaving(false);}
  };

  const deletePlan=async(planId:string)=>{
    if(!confirm("Delete this budget plan?"))return;
    setSaving(true);
    try{
      const r=await fetch(`/api/admin/budget/plans/${planId}`,{method:"DELETE",headers:authHeader()});
      const d=await r.json();
      if(d.success){showMsg("✅ Plan deleted");if(selected?.id===planId){setSelected(null);setTab("plans");}fetchPlans();}
      else showMsg("❌ "+d.error);
    }catch{showMsg("❌ Network error");}finally{setSaving(false);}
  };

  const filtered=plans.filter(p=>(fLevel==="all"||p.level===fLevel)&&(fStatus==="all"||p.status===fStatus));

  // ── Summary bar helper ────────────────────────────────────────────────────
  function SummaryBar({items,revenue,level}:{items:LineItem[];revenue:RevenueSource[];level:BudgetLevel}) {
    const cfg=BUDGET_LEVELS[level];
    const totEst=items.reduce((s,i)=>s+computeEst(i),0);
    const totBu=items.reduce((s,i)=>s+i.budgeted,0);
    const totRev=revenue.reduce((s,r)=>s+r.estimatedAmount,0);
    const surplus=totRev-totEst;
    return (
      <div style={{display:"flex",gap:16,padding:"14px 18px",background:C.card,border:`1px solid ${cfg.color}44`,borderRadius:8,flexWrap:"wrap"}}>
        <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>ESTIMATED SPEND</div><div style={{fontSize:18,fontWeight:700,color:C.blue}}>{fmt(totEst)}</div></div>
        <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>BUDGETED</div><div style={{fontSize:18,fontWeight:700,color:cfg.color}}>{fmt(totBu)}</div></div>
        <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>PROJECTED REVENUE</div><div style={{fontSize:18,fontWeight:700,color:"#4ADE80"}}>{fmt(totRev)}</div></div>
        <div><div style={{fontSize:10,color:C.muted,marginBottom:2}}>SURPLUS / DEFICIT</div><div style={{fontSize:18,fontWeight:700,color:surplus>=0?"#4ADE80":C.red}}>{surplus>=0?"+":""}{fmt(surplus)}</div></div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center"}}>
          <span style={{padding:"4px 12px",borderRadius:20,fontSize:11,fontWeight:600,background:totBu>=cfg.min&&totBu<=cfg.max?"#4ADE8022":C.surface,color:totBu>=cfg.min&&totBu<=cfg.max?"#4ADE80":C.muted,border:`1px solid ${C.border}`}}>
            Target:{cfg.range}
          </span>
        </div>
      </div>
    );
  }

  // ── Revenue Editor Row ────────────────────────────────────────────────────
  function RevRow({rev,idx,list,setList}:{key?:React.Key;rev:RevenueSource;idx:number;list:RevenueSource[];setList:(v:RevenueSource[])=>void}) {
    const updR=(patch:Partial<RevenueSource>)=>{const c=[...list];c[idx]={...c[idx],...patch};setList(c);};
    return (
      <div style={{display:"flex",gap:10,alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"10px 14px"}}>
        {rev.isCustom
          ? <input value={rev.source} onChange={e=>updR({source:e.target.value})} style={{flex:2,padding:"5px 8px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:4,color:C.text,fontSize:11}} placeholder="Revenue source"/>
          : <div style={{flex:2,fontSize:12,color:C.text,fontWeight:600}}>{rev.source}</div>
        }
        <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",minWidth:120}}>
          <div style={{fontSize:9,color:C.muted,marginBottom:2}}>ESTIMATED</div>
          <InlineAmt value={rev.estimatedAmount} onChange={v=>updR({estimatedAmount:v,projected:v})} color="#4ADE80" label="revenue"/>
        </div>
        {rev.isCustom&&<button onClick={()=>setList(list.filter(r=>r.id!==rev.id))} style={{background:"none",border:`1px solid ${C.red}33`,borderRadius:4,color:C.red,padding:"4px 8px",cursor:"pointer"}}><X size={12}/></button>}
      </div>
    );
  }

  // ── TAB: Overview ─────────────────────────────────────────────────────────
  const renderOverview=()=>(
    <div style={{display:"flex",flexDirection:"column",gap:24}}>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
        <Stat label="Total Plans"    value={String(plans.length)}       sub={`${activePlans.length} active`}/>
        <Stat label="Pending Review" value={String(pendingPlans.length)} sub="awaiting approval" color={C.orange}/>
        <Stat label="Active Budget"  value={fmt(totBudg)}               sub="across active plans"/>
        <Stat label="Actual Spend"   value={fmt(totAct)}                sub={`${pct(totAct,totBudg)} utilized`} color={totAct>totBudg?C.red:"#4ADE80"}/>
        <Stat label="Accounting Tx"  value={String(accTx.length)}       sub="linked transactions" color={C.blue}/>
      </div>

      {/* L3 Preset Banner */}
      <div style={{background:`linear-gradient(135deg,${C.green} 0%,#0a2518 100%)`,border:`1px solid ${C.gold}44`,borderRadius:12,padding:"16px 20px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10}}>
          <div>
            <div style={{fontFamily:"Cinzel,serif",color:C.gold,fontWeight:700,fontSize:14,marginBottom:4}}>🔴 L3 Grand Budget Preset — ERUE Festival 2026</div>
            <div style={{color:C.goldLt,fontSize:13}}>Expenditure: <strong>₦76,650,000</strong> · Revenue: <strong>₦83,000,000</strong> · Surplus: <strong style={{color:"#4ADE80"}}>₦6,350,000</strong></div>
            <div style={{color:"rgba(237,232,220,0.5)",fontSize:11,marginTop:4}}>10 expenditure heads · 10 revenue sources · Full Board authorization required</div>
          </div>
          {isAdmin&&plans.filter(p=>p.level==="grand").length===0&&(
            <button onClick={()=>{setNLevel("grand");setNItems(makeL3Items());setNRevenue(makeL3Revenue());setNTitle("ERUE Festival 2026 — Grand Budget");setTab("new");}}
              style={{padding:"10px 20px",background:C.gold,border:"none",borderRadius:8,color:"#000",fontWeight:700,fontSize:13,cursor:"pointer",fontFamily:"Cinzel,serif"}}>
              ➕ Create L3 Grand Plan
            </button>
          )}
        </div>
      </div>

      {/* Level Cards */}
      <div>
        <div style={{fontSize:13,color:C.gold,fontWeight:700,marginBottom:14,fontFamily:"Cinzel,serif",letterSpacing:1}}>BUDGET LEVELS</div>
        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
          {(Object.entries(BUDGET_LEVELS) as [BudgetLevel,typeof BUDGET_LEVELS[BudgetLevel]][]).map(([lvl,cfg])=>{
            const lp=plans.filter(p=>p.level===lvl);const la=lp.filter(p=>p.status==="active");
            return (
              <div key={lvl} style={{flex:1,minWidth:200,background:C.card,border:`1px solid ${cfg.color}33`,borderRadius:12,padding:"18px 20px",borderTop:`3px solid ${cfg.color}`}}>
                <div style={{fontSize:22,marginBottom:8}}>{cfg.emoji}</div>
                <div style={{fontSize:14,fontWeight:700,color:cfg.color,fontFamily:"Cinzel,serif",marginBottom:4}}>{cfg.label}</div>
                <div style={{fontSize:19,fontWeight:700,color:C.text,marginBottom:6}}>{cfg.range}</div>
                <div style={{fontSize:11,color:C.muted,marginBottom:12}}>Approver: {cfg.approver}</div>
                <div style={{display:"flex",gap:8,fontSize:11}}>
                  <span style={{background:C.surface,padding:"3px 8px",borderRadius:4,color:C.muted}}>{lp.length} plans</span>
                  <span style={{background:"#4ADE8022",padding:"3px 8px",borderRadius:4,color:"#4ADE80"}}>{la.length} active</span>
                </div>
                {isAdmin&&(
                  <button onClick={()=>{setNLevel(lvl);setNItems(lvl==="grand"?makeL3Items():makeBlankItems());setNRevenue(lvl==="grand"?makeL3Revenue():makeBlankRevenue());setTab("new");}}
                    style={{marginTop:12,width:"100%",padding:"7px 0",background:cfg.color+"22",border:`1px solid ${cfg.color}44`,borderRadius:6,color:cfg.color,fontSize:11,fontWeight:600,cursor:"pointer"}}>
                    + New {cfg.label}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Plans */}
      {plans.length>0&&(
        <div>
          <div style={{fontSize:13,color:C.gold,fontWeight:700,marginBottom:14,fontFamily:"Cinzel,serif",letterSpacing:1}}>RECENT PLANS</div>
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {plans.slice(-5).reverse().map(p=>{
              const cfg=BUDGET_LEVELS[p.level];
              return (
                <div key={p.id} onClick={()=>{setSelected(p);setTab("detail");}}
                  style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"center",gap:12}}
                  onMouseEnter={e=>(e.currentTarget as HTMLElement).style.borderColor=C.gold+"66"}
                  onMouseLeave={e=>(e.currentTarget as HTMLElement).style.borderColor=C.border}>
                  <span style={{fontSize:18}}>{cfg.emoji}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:600,color:C.text}}>{p.title}</div>
                    <div style={{fontSize:11,color:C.muted}}>{cfg.label} · Budget:{fmt(p.totalBudgeted)} · Est:{fmt(p.totalEstimatedExpenditure||0)}</div>
                  </div>
                  <SBadge status={p.status}/>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  // ── TAB: Plans ────────────────────────────────────────────────────────────
  const renderPlans=()=>(
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <div style={{display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
        <select value={fLevel} onChange={e=>setFLevel(e.target.value as BudgetLevel|"all")}
          style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,padding:"6px 10px",fontSize:12}}>
          <option value="all">All Levels</option>
          {(Object.keys(BUDGET_LEVELS) as BudgetLevel[]).map(l=><option key={l} value={l}>{BUDGET_LEVELS[l].emoji} {BUDGET_LEVELS[l].label}</option>)}
        </select>
        <select value={fStatus} onChange={e=>setFStatus(e.target.value as BudgetStatus|"all")}
          style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,padding:"6px 10px",fontSize:12}}>
          <option value="all">All Statuses</option>
          {(Object.keys(STATUS_CONFIG) as BudgetStatus[]).map(s=><option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
        </select>
        <span style={{fontSize:11,color:C.muted}}>{filtered.length} plan{filtered.length!==1?"s":""}</span>
        {isAdmin&&<button onClick={()=>setTab("new")} style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:C.gold,border:"none",borderRadius:6,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer"}}><PlusCircle size={13}/> New Plan</button>}
      </div>

      {filtered.length===0
        ? <div style={{textAlign:"center",padding:"48px 24px",background:C.card,borderRadius:12,border:`1px solid ${C.border}`,color:C.muted,fontSize:13}}>
            <DollarSign size={40} style={{margin:"0 auto 12px",opacity:0.3}}/><div>No budget plans found</div>
            {isAdmin&&<button onClick={()=>setTab("new")} style={{marginTop:12,padding:"8px 20px",background:C.gold,border:"none",borderRadius:6,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer"}}>Create First Plan</button>}
          </div>
        : <div style={{display:"flex",flexDirection:"column",gap:10}}>
            {filtered.map(p=>{
              const cfg=BUDGET_LEVELS[p.level];const v=vari(p.totalBudgeted,p.totalActual);const est=p.totalEstimatedExpenditure||0;
              return (
                <div key={p.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"16px 20px",borderLeft:`4px solid ${cfg.color}`}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                    <span style={{fontSize:24}}>{cfg.emoji}</span>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                        <span style={{fontSize:14,fontWeight:700,color:C.text}}>{p.title}</span>
                        <SBadge status={p.status}/>
                        <span style={{fontSize:10,padding:"2px 8px",background:cfg.color+"22",color:cfg.color,borderRadius:10,fontWeight:600}}>{cfg.label}</span>
                      </div>
                      <div style={{marginTop:8,display:"flex",gap:16,flexWrap:"wrap",fontSize:12,color:C.muted}}>
                        <span>Est:<strong style={{color:C.blue}}>{fmt(est)}</strong></span>
                        <span>Budget:<strong style={{color:C.gold}}>{fmt(p.totalBudgeted)}</strong></span>
                        <span>Actual:<strong style={{color:C.text}}>{fmt(p.totalActual)}</strong></span>
                        <span style={{color:v>=0?"#4ADE80":C.red}}>Var:{v>=0?"+":""}{fmt(v)}</span>
                      </div>
                      {p.totalBudgeted>0&&<VBar budgeted={p.totalBudgeted} actual={p.totalActual}/>}
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={()=>{setSelected(p);setTab("detail");}} style={{padding:"6px 12px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}><Eye size={12}/> View</button>
                      {isAdmin&&p.status==="draft"&&(
                        <>
                          <button onClick={()=>{setEditing(JSON.parse(JSON.stringify(p)));setSelected(p);setTab("detail");}} style={{padding:"6px 10px",background:C.gold+"22",border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,cursor:"pointer"}}><Edit3 size={12}/></button>
                          <button onClick={()=>deletePlan(p.id)} style={{padding:"6px 10px",background:"none",border:`1px solid ${C.red}44`,borderRadius:6,color:C.red,fontSize:11,cursor:"pointer"}}><Trash2 size={12}/></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
      }
    </div>
  );

  // ── TAB: New Plan ─────────────────────────────────────────────────────────
  const renderNew=()=>{
    const warnings=coherenceCheck(nItems,nRevenue,nLevel);
    return (
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setTab("plans")} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,padding:"6px 12px",cursor:"pointer",fontSize:12}}>← Back</button>
          <h3 style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:"Cinzel,serif",margin:0}}>New Budget Plan</h3>
        </div>

        {/* Preset toggle */}
        <div style={{display:"flex",gap:10,alignItems:"center",background:C.card,border:`1px solid ${C.gold}33`,borderRadius:8,padding:"10px 14px",flexWrap:"wrap"}}>
          <span style={{fontSize:12,color:C.gold,fontWeight:700}}>🔴 L3 Grand Preset (₦76,650,000 / ₦83M revenue)</span>
          <button onClick={()=>{setNLevel("grand");setNItems(makeL3Items());setNRevenue(makeL3Revenue());setNTitle("ERUE Festival 2026 — Grand Budget");}}
            style={{padding:"5px 12px",background:C.gold,border:"none",borderRadius:6,color:"#000",fontSize:11,fontWeight:700,cursor:"pointer"}}>Load L3 Preset</button>
          <button onClick={()=>{setNItems(makeBlankItems());setNRevenue(makeBlankRevenue());}}
            style={{padding:"5px 12px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,fontSize:11,cursor:"pointer"}}>Clear / Blank</button>
        </div>

        {/* Level */}
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {(Object.entries(BUDGET_LEVELS) as [BudgetLevel,typeof BUDGET_LEVELS[BudgetLevel]][]).map(([lvl,lcfg])=>(
            <button key={lvl} onClick={()=>setNLevel(lvl)}
              style={{flex:1,minWidth:150,padding:"12px 16px",cursor:"pointer",background:nLevel===lvl?lcfg.color+"22":C.card,border:`2px solid ${nLevel===lvl?lcfg.color:C.border}`,borderRadius:8,color:nLevel===lvl?lcfg.color:C.muted,fontWeight:700,fontSize:12}}>
              {lcfg.emoji} {lcfg.label}<br/><span style={{fontSize:11,fontWeight:400}}>{lcfg.range}</span>
            </button>
          ))}
        </div>

        {/* Title + Notes */}
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div>
            <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>PLAN TITLE *</label>
            <input value={nTitle} onChange={e=>setNTitle(e.target.value)} placeholder="e.g. ERUE 2026 Grand Budget"
              style={{width:"100%",padding:"10px 14px",boxSizing:"border-box",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13}}/>
          </div>
          <div>
            <label style={{fontSize:11,color:C.muted,display:"block",marginBottom:4}}>NOTES</label>
            <textarea value={nNotes} onChange={e=>setNNotes(e.target.value)} rows={2}
              style={{width:"100%",padding:"10px 14px",boxSizing:"border-box",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,fontSize:13,resize:"vertical"}}/>
          </div>
        </div>

        <SummaryBar items={nItems} revenue={nRevenue} level={nLevel}/>
        <CoherencePanel warnings={warnings}/>

        {/* Expenditure */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:13,color:C.gold,fontWeight:700,fontFamily:"Cinzel,serif",letterSpacing:1}}>EXPENDITURE LINE ITEMS</div>
            {isAdmin&&<button onClick={()=>setNItems(p=>[...p,{id:uid(),category:"Custom Item",description:"",estimatedAmount:0,budgeted:0,actual:0,participants:0,unitCost:0,attendeeCategories:defaultCats(),activityLevel:"L2",isCustom:true}])}
              style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",background:C.gold+"22",border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,cursor:"pointer"}}>
              <Plus size={11}/> Add Custom Item
            </button>}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {nItems.map((item,idx)=>(
              <ItemRow key={item.id} item={item} idx={idx} items={nItems} setItems={setNItems}
                expanded={expandedIdx===idx} onToggle={()=>setExpandedIdx(expandedIdx===idx?null:idx)}/>
            ))}
          </div>
        </div>

        {/* Revenue */}
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
            <div style={{fontSize:13,color:"#4ADE80",fontWeight:700,fontFamily:"Cinzel,serif",letterSpacing:1}}>PROJECTED REVENUE SOURCES</div>
            {isAdmin&&<button onClick={()=>setNRevenue(p=>[...p,{id:uid(),source:"Custom Revenue",estimatedAmount:0,projected:0,received:0,isCustom:true}])}
              style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",background:"#4ADE8022",border:"1px solid #4ADE8044",borderRadius:6,color:"#4ADE80",fontSize:11,cursor:"pointer"}}>
              <Plus size={11}/> Add Source
            </button>}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {nRevenue.map((rev,idx)=><RevRow key={rev.id} rev={rev} idx={idx} list={nRevenue} setList={setNRevenue}/>)}
          </div>
        </div>

        <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
          <button onClick={()=>setTab("plans")} style={{padding:"10px 20px",background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,cursor:"pointer",fontSize:13}}>Cancel</button>
          <button onClick={handleCreate} disabled={saving||!nTitle.trim()}
            style={{padding:"10px 24px",background:saving||!nTitle.trim()?C.border:C.gold,border:"none",borderRadius:6,color:saving||!nTitle.trim()?C.muted:"#000",cursor:saving||!nTitle.trim()?"not-allowed":"pointer",fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            {saving?<><RefreshCw size={13} style={{animation:"spin 1s linear infinite"}}/> Saving...</>:<><FileText size={13}/> Create Budget Plan</>}
          </button>
        </div>
      </div>
    );
  };

  // ── TAB: Detail ───────────────────────────────────────────────────────────
  const renderDetail=()=>{
    const p=editing||selected;
    if(!p)return <div style={{color:C.muted,padding:24}}>No plan selected</div>;
    const cfg=BUDGET_LEVELS[p.level];
    const v=vari(p.totalBudgeted,p.totalActual);
    const est=p.totalEstimatedExpenditure||0;
    const isEd=!!editing&&editing.id===selected?.id;
    const warnings=isEd?coherenceCheck(editing!.lineItems,editing!.revenueSources,editing!.level):[];
    const linkedTx=accTx.filter((tx:any)=>
      tx.budgetPlanId===p.id||
      p.lineItems.some((li:LineItem)=>li.category.toLowerCase().includes((tx.category||"").toLowerCase()))
    );

    return (
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <button onClick={()=>{setTab("plans");setEditing(null);setExpandedIdx(null);}} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,padding:"6px 12px",cursor:"pointer",fontSize:12}}>← Back</button>
          <h3 style={{fontSize:16,fontWeight:700,color:C.gold,fontFamily:"Cinzel,serif",margin:0,flex:1}}>{cfg.emoji} {p.title}</h3>
          <SBadge status={p.status}/>
          <PrintButton
            label="Print Plan"
            size="sm"
            variant="ghost"
            onClick={()=>printBudgetPlan({
              title: p.title,
              level: p.level,
              status: p.status,
              totalBudgeted: p.totalBudgeted,
              totalActual: p.totalActual,
              totalEstimatedExpenditure: p.totalEstimatedExpenditure||0,
              totalEstimatedRevenue: p.totalEstimatedRevenue||0,
              lineItems: (p.lineItems||[]).map((i:any)=>({
                category: i.category,
                description: i.description,
                activityLevel: i.activityLevel,
                estimatedAmount: i.estimatedAmount||0,
                budgeted: i.budgeted||0,
                actual: i.actual||0,
              })),
              revenueSources: (p.revenueSources||[]).map((r:any)=>({
                source: r.source,
                estimatedAmount: r.estimatedAmount||0,
                received: r.received||0,
              })),
              createdBy: p.createdBy as string,
              createdAt: p.createdAt as string,
              notes: p.notes as string,
            })}
          />
          {isAdmin&&p.status==="draft"&&!isEd&&(
            <button onClick={()=>{setEditing(JSON.parse(JSON.stringify(p)));setExpandedIdx(null);}} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",background:C.gold+"22",border:`1px solid ${C.gold}`,borderRadius:6,color:C.gold,fontSize:12,fontWeight:600,cursor:"pointer"}}>
              <Edit3 size={12}/> Edit Plan
            </button>
          )}
          {isEd&&(
            <div style={{display:"flex",gap:8}}>
              <button onClick={handleSaveEdit} disabled={saving} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",background:"#4ADE80",border:"none",borderRadius:6,color:"#000",fontSize:12,fontWeight:700,cursor:"pointer"}}><Save size={12}/> Save</button>
              <button onClick={()=>{setEditing(null);setExpandedIdx(null);}} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,fontSize:12,cursor:"pointer"}}><X size={12}/> Cancel</button>
            </div>
          )}
        </div>

        {isEd&&<CoherencePanel warnings={warnings}/>}
        {isEd&&<SummaryBar items={editing!.lineItems} revenue={editing!.revenueSources} level={editing!.level}/>}

        {/* KPIs */}
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <Stat label="Estimated"    value={fmt(est)}   color={C.blue}/>
          <Stat label="Budgeted"     value={fmt(p.totalBudgeted)} color={cfg.color}/>
          <Stat label="Actual Spend" value={fmt(p.totalActual)}   color={p.totalActual>p.totalBudgeted?C.red:"#4ADE80"}/>
          <Stat label="Variance"     value={(v>=0?"+":"")+fmt(v)} sub={v>=0?"Under budget":"Over budget"} color={v>=0?"#4ADE80":C.red}/>
          <Stat label="Utilization"  value={pct(p.totalActual,p.totalBudgeted)}/>
        </div>

        {/* Workflow */}
        {(isCWC||isBoard)&&!isEd&&(
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:16}}>
            <div style={{fontSize:12,color:C.gold,fontWeight:700,marginBottom:12,fontFamily:"Cinzel,serif",letterSpacing:1}}>WORKFLOW ACTIONS</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {p.status==="draft"&&isCWC&&<button onClick={()=>workflowAction(p.id,"submit")} disabled={saving} style={{padding:"8px 16px",background:C.orange+"22",border:`1px solid ${C.orange}`,borderRadius:6,color:C.orange,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><Send size={12}/> Submit for CWC Review</button>}
              {p.status==="cwc_review"&&isCWC&&(<>
                <button onClick={()=>workflowAction(p.id,"cwc_approve")} disabled={saving} style={{padding:"8px 16px",background:"#4ADE8022",border:"1px solid #4ADE80",borderRadius:6,color:"#4ADE80",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><ThumbsUp size={12}/> CWC Approve</button>
                <button onClick={()=>workflowAction(p.id,"reject")} disabled={saving} style={{padding:"8px 16px",background:C.red+"22",border:`1px solid ${C.red}`,borderRadius:6,color:C.red,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><ThumbsDown size={12}/> Reject</button>
              </>)}
              {p.status==="board_review"&&isBoard&&(<>
                <button onClick={()=>workflowAction(p.id,"board_authorize")} disabled={saving} style={{padding:"8px 16px",background:C.purple+"22",border:`1px solid ${C.purple}`,borderRadius:6,color:C.purple,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><Shield size={12}/> Board Authorize</button>
                <button onClick={()=>workflowAction(p.id,"reject")} disabled={saving} style={{padding:"8px 16px",background:C.red+"22",border:`1px solid ${C.red}`,borderRadius:6,color:C.red,fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><ThumbsDown size={12}/> Reject</button>
              </>)}
              {p.status==="authorized"&&isBoard&&<button onClick={()=>workflowAction(p.id,"activate")} disabled={saving} style={{padding:"8px 16px",background:"#4ADE8022",border:"1px solid #4ADE80",borderRadius:6,color:"#4ADE80",fontSize:12,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:5}}><CheckCircle size={12}/> Set Active</button>}
            </div>
          </div>
        )}

        {/* Line Items */}
        {p.lineItems.length>0&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:12,color:C.gold,fontWeight:700,fontFamily:"Cinzel,serif",letterSpacing:1}}>EXPENDITURE LINE ITEMS</div>
              {isEd&&editing&&<button onClick={()=>setEditing(ep=>ep?{...ep,lineItems:[...ep.lineItems,{id:uid(),category:"Custom Item",description:"",estimatedAmount:0,budgeted:0,actual:0,participants:0,unitCost:0,attendeeCategories:defaultCats(),activityLevel:"L2",isCustom:true}]}:null)}
                style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",background:C.gold+"22",border:`1px solid ${C.gold}44`,borderRadius:6,color:C.gold,fontSize:11,cursor:"pointer"}}>
                <Plus size={11}/> Add Item
              </button>}
            </div>
            {isEd&&editing
              ? <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {editing.lineItems.map((item,idx)=>(
                    <ItemRow key={item.id} item={item} idx={idx}
                      items={editing.lineItems}
                      setItems={items=>setEditing(ep=>ep?{...ep,lineItems:items,totalBudgeted:items.reduce((s,i)=>s+i.budgeted,0)}:null)}
                      expanded={expandedIdx===idx} onToggle={()=>setExpandedIdx(expandedIdx===idx?null:idx)}/>
                  ))}
                </div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {p.lineItems.map(item=>{
                    const ev=computeEst(item);const iv=vari(item.budgeted,item.actual);
                    return (
                      <div key={item.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"12px 16px"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                          <div>
                            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                              <LBadge level={item.activityLevel}/>
                              <span style={{fontSize:12,fontWeight:600,color:C.text}}>{item.category}</span>
                            </div>
                            {item.description&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{item.description}</div>}
                            {item.participants>0&&<div style={{fontSize:10,color:C.blue,marginTop:2}}>{item.participants.toLocaleString()} participants × {fmt(item.unitCost)}</div>}
                          </div>
                          <div style={{textAlign:"right"}}>
                            <div style={{fontSize:11,color:C.muted,marginBottom:2}}>Est:<span style={{color:C.blue}}>{fmt(ev)}</span></div>
                            <div style={{fontSize:13,fontWeight:700,color:C.gold}}>Budget:{fmt(item.budgeted)}</div>
                            <div style={{fontSize:10,color:iv>=0?"#4ADE80":C.red}}>Var:{iv>=0?"+":""}{fmt(iv)}</div>
                          </div>
                        </div>
                        <VBar budgeted={item.budgeted} actual={item.actual}/>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        )}

        {/* Revenue */}
        {p.revenueSources.length>0&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
              <div style={{fontSize:12,color:"#4ADE80",fontWeight:700,fontFamily:"Cinzel,serif",letterSpacing:1}}>REVENUE SOURCES</div>
              {isEd&&editing&&<button onClick={()=>setEditing(ep=>ep?{...ep,revenueSources:[...ep.revenueSources,{id:uid(),source:"Custom Revenue",estimatedAmount:0,projected:0,received:0,isCustom:true}]}:null)}
                style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",background:"#4ADE8022",border:"1px solid #4ADE8044",borderRadius:6,color:"#4ADE80",fontSize:11,cursor:"pointer"}}>
                <Plus size={11}/> Add Source
              </button>}
            </div>
            {isEd&&editing
              ? <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {editing.revenueSources.map((rev,idx)=>(
                    <RevRow key={rev.id} rev={rev} idx={idx}
                      list={editing.revenueSources}
                      setList={rs=>setEditing(ep=>ep?{...ep,revenueSources:rs}:null)}/>
                  ))}
                </div>
              : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {p.revenueSources.map(rev=>(
                    <div key={rev.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"10px 14px"}}>
                      <span style={{fontSize:12,color:C.text}}>{rev.source}</span>
                      <div style={{textAlign:"right"}}>
                        <div style={{fontSize:11,color:C.muted}}>Est:<span style={{color:C.blue}}>{fmt(rev.estimatedAmount||0)}</span></div>
                        <div style={{fontSize:12,fontWeight:700,color:"#4ADE80"}}>{fmt(rev.received)}/{fmt(rev.projected)}</div>
                        <div style={{fontSize:10,color:C.muted}}>{pct(rev.received,rev.projected)} collected</div>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        )}

        {/* Linked Accounting */}
        <div>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <div style={{fontSize:12,color:C.blue,fontWeight:700,fontFamily:"Cinzel,serif",letterSpacing:1}}>🔗 LINKED ACCOUNTING TRANSACTIONS</div>
            <span style={{fontSize:10,background:C.blue+"22",color:C.blue,padding:"2px 8px",borderRadius:10}}>{linkedTx.length}</span>
          </div>
          {linkedTx.length===0
            ? <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:8,padding:"16px 20px",color:C.muted,fontSize:12,textAlign:"center"}}>No accounting transactions linked yet — transactions from the Accounting module appear here when categories match.</div>
            : <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {linkedTx.slice(0,10).map((tx:any,i:number)=>(
                  <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"10px 14px"}}>
                    <div>
                      <div style={{fontSize:12,color:C.text,fontWeight:600}}>{tx.description||tx.category||"Transaction"}</div>
                      <div style={{fontSize:10,color:C.muted}}>{tx.category}·{tx.date||tx.createdAt||""}</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700,color:tx.type==="income"?"#4ADE80":C.red}}>{tx.type==="income"?"+":"-"}{fmt(tx.amount||0)}</div>
                      <div style={{fontSize:10,color:C.muted}}>{tx.type}</div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>

        {/* Approval History */}
        {p.approvalHistory.length>0&&(
          <div>
            <div style={{fontSize:12,color:C.gold,fontWeight:700,marginBottom:10,fontFamily:"Cinzel,serif",letterSpacing:1}}>APPROVAL HISTORY</div>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {p.approvalHistory.map((step,i)=>(
                <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start",background:C.card,border:`1px solid ${C.border}`,borderRadius:6,padding:"10px 14px"}}>
                  <CheckCircle size={14} style={{color:C.gold,marginTop:1,flexShrink:0}}/>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:C.text}}>{step.step} — <span style={{color:C.gold}}>{step.action}</span></div>
                    <div style={{fontSize:11,color:C.muted}}>{step.actor}·{step.role}·{new Date(step.timestamp).toLocaleDateString()}</div>
                    {step.notes&&<div style={{fontSize:11,color:C.muted,marginTop:3}}>{step.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── TAB: Variance ─────────────────────────────────────────────────────────
  const renderVariance=()=>{
    const allItems=plans.flatMap(p=>p.lineItems.map(i=>({...i,planTitle:p.title})));
    const byCat:Record<string,{budgeted:number;actual:number;estimated:number}>={};
    allItems.forEach(i=>{
      if(!byCat[i.category])byCat[i.category]={budgeted:0,actual:0,estimated:0};
      byCat[i.category].budgeted+=i.budgeted;byCat[i.category].actual+=i.actual;byCat[i.category].estimated+=computeEst(i);
    });
    const tB=Object.values(byCat).reduce((s,v)=>s+v.budgeted,0);
    const tA=Object.values(byCat).reduce((s,v)=>s+v.actual,0);
    const tE=Object.values(byCat).reduce((s,v)=>s+v.estimated,0);
    const allRev=plans.flatMap(p=>p.revenueSources);
    const tR=allRev.reduce((s,r)=>s+(r.estimatedAmount||0),0);
    const gW:Warning[]=[];
    if(tR>0&&tR<tE)gW.push({type:"error",msg:`GLOBAL DEFICIT: Revenue estimates (${fmt(tR)}) < Estimated expenditure (${fmt(tE)}) — deficit ${fmt(tE-tR)}`});
    if(tA>tB)gW.push({type:"error",msg:`OVERSPEND: Actual (${fmt(tA)}) exceeds Budgeted (${fmt(tB)}) by ${fmt(tA-tB)}`});

    return (
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <Stat label="Total Estimated" value={fmt(tE)} color={C.blue}/>
          <Stat label="Total Budgeted"  value={fmt(tB)}/>
          <Stat label="Total Actual"    value={fmt(tA)} color={tA>tB?C.red:"#4ADE80"}/>
          <Stat label="Net Variance"    value={(vari(tB,tA)>=0?"+":"")+fmt(vari(tB,tA))} color={vari(tB,tA)>=0?"#4ADE80":C.red}/>
        </div>
        <CoherencePanel warnings={gW}/>
      <div style={{display:"flex",justifyContent:"flex-end",marginTop:8,marginBottom:4}}>
        <PrintButton
          label="Print Variance Report"
          size="sm"
          variant="ghost"
          onClick={()=>printVarianceReport(
            Object.entries(byCat).map(([cat,vals])=>({
              category: cat,
              estimated: (vals as {estimated:number;budgeted:number;actual:number}).estimated||0,
              budgeted:  (vals as {estimated:number;budgeted:number;actual:number}).budgeted||0,
              actual:    (vals as {estimated:number;budgeted:number;actual:number}).actual||0,
            })),
            { estimated: tE, budgeted: tB, actual: tA }
          )}
        />
      </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",gap:10}}>
            {["Category","Estimated","Budgeted","Actual","Variance","%"].map((h,i)=>(
              <div key={h} style={{flex:i===0?2:1,fontSize:11,color:C.muted,textTransform:"uppercase",textAlign:i===0?"left":"right"}}>{h}</div>
            ))}
          </div>
          {Object.entries(byCat).length===0
            ? <div style={{padding:"32px 16px",textAlign:"center",color:C.muted,fontSize:13}}>No line items across any budget plans yet</div>
            : Object.entries(byCat).map(([cat,vals])=>{
                const iv=vari(vals.budgeted,vals.actual);const u=vals.budgeted>0?((vals.actual/vals.budgeted)*100).toFixed(1):"—";
                return (
                  <div key={cat} style={{display:"flex",gap:10,padding:"10px 16px",borderBottom:`1px solid ${C.border}`,alignItems:"center"}}>
                    <div style={{flex:2,fontSize:12,color:C.text}}>{cat}</div>
                    <div style={{flex:1,fontSize:12,color:C.blue,textAlign:"right"}}>{fmt(vals.estimated)}</div>
                    <div style={{flex:1,fontSize:12,color:C.gold,textAlign:"right"}}>{fmt(vals.budgeted)}</div>
                    <div style={{flex:1,fontSize:12,color:C.text,textAlign:"right"}}>{fmt(vals.actual)}</div>
                    <div style={{flex:1,fontSize:12,color:iv>=0?"#4ADE80":C.red,textAlign:"right",fontWeight:600}}>{iv>=0?"+":""}{fmt(iv)}</div>
                    <div style={{flex:1,fontSize:12,color:C.muted,textAlign:"right"}}>{u}{u!=="—"?"%":""}</div>
                  </div>
                );
              })
          }
          {Object.entries(byCat).length>0&&(
            <div style={{display:"flex",gap:10,padding:"12px 16px",background:C.surface,fontWeight:700}}>
              <div style={{flex:2,fontSize:12,color:C.gold}}>TOTAL</div>
              <div style={{flex:1,fontSize:12,color:C.blue,textAlign:"right"}}>{fmt(tE)}</div>
              <div style={{flex:1,fontSize:12,color:C.gold,textAlign:"right"}}>{fmt(tB)}</div>
              <div style={{flex:1,fontSize:12,color:C.text,textAlign:"right"}}>{fmt(tA)}</div>
              <div style={{flex:1,fontSize:12,color:vari(tB,tA)>=0?"#4ADE80":C.red,textAlign:"right"}}>{vari(tB,tA)>=0?"+":""}{fmt(vari(tB,tA))}</div>
              <div style={{flex:1,fontSize:12,color:C.muted,textAlign:"right"}}>{tB>0?((tA/tB)*100).toFixed(1)+"%":"—"}</div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── TAB: Accounting ───────────────────────────────────────────────────────
  const renderAccounting=()=>{
    const inc=accTx.filter((t:any)=>t.type==="income");
    const exp=accTx.filter((t:any)=>t.type==="expense"||t.type==="expenditure");
    const tI=inc.reduce((s:number,t:any)=>s+(t.amount||0),0);
    const tE2=exp.reduce((s:number,t:any)=>s+(t.amount||0),0);
    return (
      <div style={{display:"flex",flexDirection:"column",gap:20}}>
        <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
          <Stat label="Income Tx"       value={String(inc.length)} sub={fmt(tI)}     color="#4ADE80"/>
          <Stat label="Expenditure Tx"  value={String(exp.length)} sub={fmt(tE2)}    color={C.red}/>
          <Stat label="Net Balance"     value={fmt(tI-tE2)}        color={tI-tE2>=0?"#4ADE80":C.red}/>
          <Stat label="Total Tx"        value={String(accTx.length)} color={C.blue}/>
        </div>
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:"hidden"}}>
          <div style={{padding:"12px 16px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:13,color:C.gold,fontWeight:700,fontFamily:"Cinzel,serif"}}>🔗 All Accounting Transactions</div>
            <button onClick={fetchAcc} style={{background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,padding:"5px 10px",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:4}}><RefreshCw size={11}/> Refresh</button>
          </div>
          {accTx.length===0
            ? <div style={{padding:"32px 16px",textAlign:"center",color:C.muted,fontSize:13}}>No accounting transactions found. Transactions from the Accounting Dashboard will appear here.</div>
            : accTx.slice(0,50).map((tx:any,i:number)=>(
                <div key={i} style={{display:"flex",gap:12,padding:"10px 16px",borderBottom:`1px solid ${C.border}`,alignItems:"center"}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:tx.type==="income"?"#4ADE80":C.red,flexShrink:0}}/>
                  <div style={{flex:2}}>
                    <div style={{fontSize:12,color:C.text,fontWeight:600}}>{tx.description||"Transaction"}</div>
                    <div style={{fontSize:10,color:C.muted}}>{tx.category}·{tx.date||tx.createdAt||""}</div>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:tx.type==="income"?"#4ADE80":C.red}}>{tx.type==="income"?"+":"-"}{fmt(tx.amount||0)}</div>
                    <div style={{fontSize:10,color:C.muted,textTransform:"uppercase"}}>{tx.type}</div>
                  </div>
                </div>
              ))
          }
        </div>
      </div>
    );
  };

  // ── Guard ─────────────────────────────────────────────────────────────────
  if(!currentUser) return (
    <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center",background:C.bg}}>
      <div style={{textAlign:"center",padding:"40px 32px",background:C.card,border:`1px solid ${C.gold}33`,borderRadius:16,maxWidth:380}}>
        <DollarSign size={48} style={{color:C.gold,margin:"0 auto 16px"}}/>
        <h2 style={{color:C.gold,fontFamily:"Cinzel,serif",marginBottom:8,fontSize:18}}>Budget Module</h2>
        <p style={{color:C.muted,fontSize:13,lineHeight:1.6}}>Staff login required to access the ERUE Festival Budget Manager.</p>
      </div>
    </div>
  );

  const TABS=[
    {id:"overview"   as const,label:"Overview",       icon:<LayoutDashboard size={14}/>},
    {id:"plans"      as const,label:"Budget Plans",   icon:<FileText size={14}/>},
    {id:"variance"   as const,label:"Variance Report",icon:<BarChart3 size={14}/>},
    {id:"accounting" as const,label:"Accounting Link",icon:<Link size={14}/>},
    ...(isAdmin?[{id:"new" as const,label:"New Plan",icon:<PlusCircle size={14}/>}]:[]),
  ];

  return (
    <div style={{background:C.bg,minHeight:"80vh",paddingBottom:40}}>
      <div style={{background:C.surface,borderBottom:`1px solid ${C.border}`,padding:"20px 24px 0"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16,flexWrap:"wrap",gap:8}}>
          <div>
            <h2 style={{fontSize:20,fontWeight:700,color:C.gold,fontFamily:"Cinzel,serif",margin:0}}>💰 Budget Manager</h2>
            <p style={{fontSize:12,color:C.muted,margin:"4px 0 0"}}>ERUE Festival 2026 · DROP 32 Enhanced · Dec 24–30, 2026</p>
          </div>
          <button onClick={()=>{fetchPlans();fetchAcc();}} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 12px",background:"none",border:`1px solid ${C.border}`,borderRadius:6,color:C.muted,fontSize:11,cursor:"pointer"}}>
            <RefreshCw size={12}/> Refresh
          </button>
        </div>
        <div style={{display:"flex",gap:2,overflowX:"auto"}}>
          {TABS.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{display:"flex",alignItems:"center",gap:5,padding:"8px 16px",background:"none",border:"none",borderBottom:tab===t.id?`2px solid ${C.gold}`:"2px solid transparent",color:tab===t.id?C.gold:C.muted,fontSize:12,fontWeight:tab===t.id?700:400,cursor:"pointer",whiteSpace:"nowrap"}}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>
      </div>

      {msg&&(
        <div style={{margin:"0 24px",padding:"10px 16px",background:msg.startsWith("✅")?"#4ADE8022":msg.startsWith("❌")?C.red+"22":C.orange+"22",border:`1px solid ${msg.startsWith("✅")?"#4ADE8044":msg.startsWith("❌")?C.red+"44":C.orange+"44"}`,borderRadius:6,color:C.text,fontSize:12,marginTop:12}}>
          {msg}
        </div>
      )}

      <div style={{padding:"24px 24px 0"}}>
        {loading
          ? <div style={{textAlign:"center",padding:"48px 0",color:C.muted}}><RefreshCw size={24} style={{animation:"spin 1s linear infinite",margin:"0 auto 12px"}}/><div style={{fontSize:13}}>Loading budget data...</div></div>
          : error
          ? <div style={{textAlign:"center",padding:40,background:C.card,border:`1px solid ${C.red}44`,borderRadius:10,color:C.red,fontSize:13}}>
              <AlertTriangle size={32} style={{margin:"0 auto 12px"}}/><div>{error}</div>
              <button onClick={fetchPlans} style={{marginTop:12,padding:"8px 16px",background:C.surface,border:`1px solid ${C.border}`,borderRadius:6,color:C.text,cursor:"pointer",fontSize:12}}>Retry</button>
            </div>
          : <>
              {tab==="overview"   && renderOverview()}
              {tab==="plans"      && renderPlans()}
              {tab==="new"        && isAdmin && renderNew()}
              {tab==="detail"     && renderDetail()}
              {tab==="variance"   && renderVariance()}
              {tab==="accounting" && renderAccounting()}
            </>
        }
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
